/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { Editor } from '@ckeditor/ckeditor5-core/src/editor/editor';
import React, { FC, useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import { renderToStaticMarkup } from 'react-dom/server';
import { v4 as uuidv4 } from 'uuid';
import {
  MentionData,
  MentionPopup,
  OnUserMention,
} from 'components/molecules/mention';
import { MentionProvider } from 'components/molecules/mention/mention-store';
import { TaskName } from 'components/organisms/templates/commons/name-field';
import { useCKMentionEvents } from 'hooks/use-ck-mention-events';
import { ClickEventTags, setMarkerForDataDog } from 'hooks/use-data-dog-logger';
import { getTextFromHTML } from 'utils/getTextFromHTML';
import { queueMacrotask } from 'utils/macrotask';
import { MentionType, Suggestion } from 'vendor/ckeditor/plugins/mention/types';

export const CK_MENTION_POPUP = 'ck-mention-popup';

interface Props {
  renderOnPortal?: boolean;
  children: any;
  editor: Editor;
  attachTo?: HTMLElement | null;
  mentionData?: MentionData;
  updateParentElementLocation: (data) => void;
  onMentionAdd?(args: any): void;
  onUserMention?: OnUserMention;
  projectId?: number | 'new';
  hideTasks?: boolean;
}

export const CKMentionPopup: FC<Props> = ({
  renderOnPortal,
  editor,
  attachTo,
  children,
  mentionData,
  updateParentElementLocation,
  onMentionAdd,
  onUserMention,
  hideTasks,
  projectId: projIdFromProp,
}) => {
  const [activeMentionTab, setActiveMentionTab] = useState<MentionType>();
  const [activeMentionQuery, setActiveMentionQuery] = useState<string>('');
  const [showMentionPopup, setMentionPopupVisibility] =
    useState<boolean>(false);

  const handleCKMentionEvents = useCallback(
    (e, data) => {
      if (e.name === 'mentionPopup:render') {
        setMarkerForDataDog(ClickEventTags.CkMentionPopup);

        const newPositions = {
          left: data.position.left,
          top: data.position.top,
        };

        updateParentElementLocation(newPositions);
        queueMacrotask(() => {
          setMentionPopupVisibility(true);
        });
      } else if (e.name === 'mentionPopup:remove') {
        setMentionPopupVisibility(false);
      }

      if (e.name === 'mentionPopup:query') {
        if (!showMentionPopup) {
          queueMacrotask(() => {
            setMentionPopupVisibility(true);
          });
        }
        if (hideTasks) {
          if (data === '@') {
            // switch to documents tab, as user typed @@ now
            setActiveMentionTab(MentionType.SpaceTab);
          } else if (!data.length) {
            // switch to People tab, as user typed @ now
            setActiveMentionTab(MentionType.User);
          }
        } else if (data === '@') {
          // switch to tasks tab, as user typed @@ now
          setActiveMentionTab(MentionType.Task);
        } else if (data === '@@') {
          // switch to documents tab, as user typed @@@ now
          setActiveMentionTab(MentionType.SpaceTab);
        } else if (!data.length) {
          // switch to People tab, as user typed @ now
          setActiveMentionTab(MentionType.User);
        }
        setActiveMentionQuery(data);
      }
    },
    [showMentionPopup, updateParentElementLocation, hideTasks],
  );

  useCKMentionEvents(handleCKMentionEvents, editor);

  const mentionSelected = (args) => {
    const { name, photoUrl, type, subType, objectId, projectId, spaceId } =
      args;
    const identifier = uuidv4();

    const { model } = editor;
    const mentionMarker = editor.model.markers.get('mention');

    // Create a range on matched text.
    const end = model.createPositionAt(model.document.selection.focus);
    const start = model.createPositionAt(mentionMarker.getStart());
    const range = model.createRange(start, end);

    // The code `projectId: projectId || projIdFromProp,` may cause bug in future -
    // if we want to support mention data from other projects. At that time projectId must be
    // coming from suggestionItem itself. Currently I'm not removing this
    // as it may cause some bug as this is the current implementation. - @asimkt
    const strippedText = getTextFromHTML(
      renderToStaticMarkup(<TaskName taskName={name} />),
    )?.replaceAll?.(/\{\{[^{}]*\}\}/g, '');

    const item = {
      id: `@${strippedText}`,
      name: strippedText,
      objectId,
      photoUrl,
      identifier,
      type,
      subType,
      spaceId,
      projectId: projectId || projIdFromProp,
    };

    const options = {
      mention: item,
      marker: '@',
      range,
    };

    editor.execute('mention', options);

    editor.editing.view.focus();

    if (onMentionAdd) {
      onMentionAdd(args);
    }
  };

  const onSelect = (args: Suggestion) => {
    const { type } = args;

    if (type === MentionType.User && onUserMention) {
      onUserMention(args, mentionSelected);
    } else {
      mentionSelected(args);
    }
  };

  const popupContent = (
    <MentionProvider>
      <MentionPopup
        data={mentionData}
        editor={editor}
        overlayClassName={CK_MENTION_POPUP}
        visible={showMentionPopup}
        activeTab={activeMentionTab}
        onTabChange={(tab) => {
          setActiveMentionTab(tab as MentionType);
        }}
        onSelect={onSelect}
        query={activeMentionQuery}
        attachTo={attachTo}
        hideTasks={hideTasks}
      >
        {children}
      </MentionPopup>
    </MentionProvider>
  );

  return renderOnPortal
    ? ReactDOM.createPortal(
        popupContent,
        document.getElementById('rl-mention-popup') || document.body,
      )
    : popupContent;
};
